import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { AdminNameCell, AdminRowActions } from '../admin/AdminRowCells';

import {
  createEntityRole,
  deleteEntityRole,
  fetchEntityRoles,
  fetchPermissionCatalog,
  updateEntityRole,
} from './entityRbacApi';
import { isSupermarketContext } from './entityContext';
import { canManageRoles } from './rbacSession';
import { confirmAction } from './ui/feedback';
import OrdersTablePagination from './OrdersTablePagination';
import OrdersSortableTh from './OrdersSortableTh';
import { buildSortHandlers, sortList, str } from './tableSortUtils';

import './css/Inventory.css';
import './css/Menu.css';
import './css/Purchase.css';
import './css/OrdersTablePagination.css';
import './css/UserManagement.css';
import './css/RoleAccessAdmin.css';

const emptyForm = {
  roleName: '',
  permissionCodes: [],
};

function getRoleSortValue(row, sortKey) {
  switch (sortKey) {
    case 'id':
      return Number(row.entityRoleId) || 0;
    case 'name':
      return str(row.roleName);
    case 'system':
      return row.isSystem ? 1 : 0;
    case 'permissions':
      return (row.permissionCodes || []).join(', ');
    default:
      return '';
  }
}

const RoleAccessAdmin = () => {
  const isStoreContext = isSupermarketContext();
  const allowMutate = canManageRoles();
  const entityId = isStoreContext
    ? localStorage.getItem('storeId') || ''
    : localStorage.getItem('restaurantId') || '';

  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listMessage, setListMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState(null);

  const loadData = useCallback(async () => {
    if (!entityId) {
      setRoles([]);
      setCatalog([]);
      setListMessage(
        isStoreContext
          ? 'No store selected. Switch store from the profile menu.'
          : 'No restaurant selected. Switch restaurant from the profile menu.'
      );
      return;
    }

    setLoading(true);
    setListMessage('');
    try {
      const [roleRows, permissionRows] = await Promise.all([
        fetchEntityRoles(entityId, { isStore: isStoreContext }),
        fetchPermissionCatalog(entityId, { isStore: isStoreContext }),
      ]);
      setRoles(roleRows);
      setCatalog(permissionRows);
    } catch (error) {
      console.error('Error loading entity roles:', error?.response?.data || error);
      setRoles([]);
      setCatalog([]);
      const status = error?.response?.status;
      const msg = error?.response?.data?.message || error?.message;
      if (status === 401) {
        setListMessage('Session expired or not signed in. Please sign in again.');
      } else if (status === 403) {
        setListMessage('You do not have permission to view roles for this business.');
      } else {
        setListMessage(msg || 'Failed to load roles. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [entityId, isStoreContext]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const pagePermissions = useMemo(
    () => catalog.filter((p) => p.kind === 'PAGE' || String(p.permissionCode).startsWith('page.')),
    [catalog]
  );
  const functionPermissions = useMemo(
    () =>
      catalog.filter(
        (p) => p.kind === 'FUNCTION' || String(p.permissionCode).startsWith('fn.')
      ),
    [catalog]
  );

  const resetForm = () => {
    setForm({ ...emptyForm });
    setEditingRole(null);
  };

  const handleOpenCreate = () => {
    if (!allowMutate) return;
    setSubmitError('');
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (role) => {
    if (!allowMutate) return;
    setSubmitError('');
    setEditingRole(role);
    setForm({
      roleName: role.roleName || '',
      permissionCodes: [...(role.permissionCodes || [])],
    });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSubmitError('');
    resetForm();
  };

  const togglePermission = (code) => {
    setForm((prev) => {
      const has = prev.permissionCodes.includes(code);
      return {
        ...prev,
        permissionCodes: has
          ? prev.permissionCodes.filter((c) => c !== code)
          : [...prev.permissionCodes, code],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allowMutate) {
      setSubmitError('You do not have permission to manage roles.');
      return;
    }
    if (!entityId) {
      setSubmitError('No business context selected.');
      return;
    }
    const roleName = form.roleName.trim();
    if (!roleName) {
      setSubmitError('Role name is required.');
      return;
    }
    if (editingRole?.isSystem && roleName !== editingRole.roleName) {
      setSubmitError('System role names cannot be renamed.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    try {
      if (editingRole) {
        await updateEntityRole(
          entityId,
          editingRole.entityRoleId,
          { roleName, permissionCodes: form.permissionCodes },
          { isStore: isStoreContext }
        );
        alert('Role updated successfully');
      } else {
        await createEntityRole(
          entityId,
          { roleName, permissionCodes: form.permissionCodes },
          { isStore: isStoreContext }
        );
        alert('Role created successfully');
      }
      await loadData();
      handleClose();
    } catch (error) {
      console.error('Error saving role:', error?.response?.data || error);
      setSubmitError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to save role. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (role) => {
    if (!allowMutate) return;
    if (role.isSystem) {
      alert('System roles cannot be deleted.');
      return;
    }
    const ok = await confirmAction({
      title: 'Delete role?',
      message: `Delete role “${role.roleName}”? Users assigned to it must be reassigned first.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;

    try {
      await deleteEntityRole(entityId, role.entityRoleId, { isStore: isStoreContext });
      alert('Role deleted successfully');
      await loadData();
    } catch (error) {
      console.error('Error deleting role:', error?.response?.data || error);
      alert(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to delete role'
      );
    }
  };

  const searchTrimmed = searchQuery.trim().toLowerCase();
  const filteredRoles = useMemo(() => {
    if (!searchTrimmed) return roles;
    return roles.filter((role) =>
      [role.entityRoleId, role.roleName, ...(role.permissionCodes || [])].some((v) =>
        String(v ?? '')
          .toLowerCase()
          .includes(searchTrimmed)
      )
    );
  }, [roles, searchTrimmed]);

  useEffect(() => {
    setPage(1);
    setSort(null);
  }, [searchTrimmed]);

  const sortedRoles = useMemo(
    () => sortList(filteredRoles, sort, getRoleSortValue),
    [filteredRoles, sort]
  );

  const { handleSortAsc, handleSortDesc } = buildSortHandlers(setSort, () => setPage(1));

  const totalPages = Math.max(1, Math.ceil(sortedRoles.length / pageSize));
  const pageSafe = Math.min(Math.max(1, page), totalPages);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages, pageSize]);

  const pageStart = (pageSafe - 1) * pageSize;
  const pagedRoles = sortedRoles.slice(pageStart, pageStart + pageSize);
  const colCount = 4;

  return (
    <div className="menu inventory-page purchase-page">
      <div className="inventory-supplier-panel user-management-panel role-access-panel">
        {listMessage ? <p className="inventory-status">{listMessage}</p> : null}

        {!allowMutate ? (
          <p className="inventory-status">
            You can view roles but need role-manage permission to create or edit them.
          </p>
        ) : null}

        {entityId ? (
          <>
            <div className="purchase-content-card">
              <div className="inventory-list-toolbar menu-toolbar">
                <div className="menu-toolbar-row user-management-toolbar-row">
                  <div className="menu-food-filter-wrap">
                    <input
                      type="text"
                      inputMode="search"
                      autoComplete="off"
                      className="menu-food-filter-input"
                      placeholder="Search roles…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      aria-label="Search roles"
                      role="searchbox"
                    />
                    {searchQuery.trim() !== '' ? (
                      <button
                        type="button"
                        className="menu-food-filter-clear"
                        aria-label="Clear search"
                        onClick={() => setSearchQuery('')}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    ) : null}
                  </div>
                  <div className="menu-toolbar-spacer" />
                  <div className="purchase-toolbar-actions">
                    {allowMutate ? (
                      <button
                        type="button"
                        className="purchase-btn purchase-btn--create"
                        onClick={handleOpenCreate}
                      >
                        Add Role
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="menu-table-wrapper">
                <table className="menu-table admin-rows-table">
                  <thead>
                    <tr>
                      <OrdersSortableTh
                        label="Name"
                        sortKey="name"
                        activeSortKey={sort?.key}
                        sortDirection={sort?.direction}
                        onSortAsc={handleSortAsc}
                        onSortDesc={handleSortDesc}
                      />
                      <OrdersSortableTh
                        label="System"
                        sortKey="system"
                        activeSortKey={sort?.key}
                        sortDirection={sort?.direction}
                        onSortAsc={handleSortAsc}
                        onSortDesc={handleSortDesc}
                      />
                      <OrdersSortableTh
                        label="Permissions"
                        sortKey="permissions"
                        activeSortKey={sort?.key}
                        sortDirection={sort?.direction}
                        onSortAsc={handleSortAsc}
                        onSortDesc={handleSortDesc}
                      />
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={colCount} className="inventory-items-hint inventory-table-empty-cell">
                          Loading roles…
                        </td>
                      </tr>
                    ) : roles.length === 0 ? (
                      <tr>
                        <td colSpan={colCount} className="inventory-items-hint inventory-table-empty-cell">
                          No roles yet.
                        </td>
                      </tr>
                    ) : sortedRoles.length === 0 ? (
                      <tr>
                        <td colSpan={colCount} className="inventory-items-hint inventory-table-empty-cell">
                          No roles match your search.
                        </td>
                      </tr>
                    ) : (
                      pagedRoles.map((role) => (
                        <tr key={role.entityRoleId}>
                          <td>
                            <AdminNameCell name={role.roleName || '—'} id={role.entityRoleId} />
                          </td>
                          <td>{role.isSystem ? 'Yes' : 'No'}</td>
                          <td>
                            <div className="user-role-list role-access-permission-list">
                              {(role.permissionCodes || []).map((code) => (
                                <span key={code} className="user-role-pill">
                                  {code}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="menu-table-actions-cell">
                            {allowMutate ? (
                              <AdminRowActions
                                name={role.roleName || 'role'}
                                onEdit={() => handleEdit(role)}
                                onDeactivate={!role.isSystem ? () => handleDelete(role) : undefined}
                                deactivateLabel="Delete"
                              />
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {!loading && sortedRoles.length > 0 ? (
                <OrdersTablePagination
                  currentPage={pageSafe}
                  totalPages={totalPages}
                  totalItems={sortedRoles.length}
                  pageSize={pageSize}
                  onPageChange={(next) => {
                    setPage(next);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  onPageSizeChange={(nextSize) => {
                    setPageSize(nextSize);
                    setPage(1);
                  }}
                  itemLabel="roles"
                  ariaLabel="Roles table pagination"
                />
              ) : null}
            </div>
          </>
        ) : null}

        {showModal ? (
          <div className="inventory-modal-overlay">
            <div
              className="inventory-modal inventory-user-modal role-access-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="role-access-modal-title"
            >
              <div className="inventory-modal-header">
                <h4 id="role-access-modal-title">
                  {editingRole ? 'Edit Role' : 'Add Role'}
                </h4>
                <button
                  type="button"
                  className="menu-template-modal-close"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>

              {submitError ? <p className="inventory-status">{submitError}</p> : null}

              <form className="inventory-form inventory-form--user-modal" onSubmit={handleSubmit}>
                <div className="inventory-form-group inventory-form-span-full">
                  <label htmlFor="role-name" className="inventory-form-label--required">
                    Role name
                  </label>
                  <input
                    id="role-name"
                    type="text"
                    className="add-menu-form-field"
                    value={form.roleName}
                    onChange={(e) => setForm((prev) => ({ ...prev, roleName: e.target.value }))}
                    disabled={Boolean(editingRole?.isSystem)}
                    required
                  />
                  {editingRole?.isSystem ? (
                    <p className="user-activate-hint">System role names cannot be renamed.</p>
                  ) : null}
                </div>

                <div className="inventory-form-group inventory-form-span-full role-permission-matrix">
                  <span className="user-role-checkboxes-label">Pages</span>
                  <div className="user-role-checkboxes-row">
                    {pagePermissions.length === 0 ? (
                      <span className="inventory-items-hint">No page permissions in catalog.</span>
                    ) : (
                      pagePermissions.map((perm) => (
                        <label key={perm.permissionCode} className="inventory-checkbox-label">
                          <input
                            type="checkbox"
                            checked={form.permissionCodes.includes(perm.permissionCode)}
                            onChange={() => togglePermission(perm.permissionCode)}
                          />
                          <span>
                            {perm.name}
                            <span className="role-permission-code"> ({perm.permissionCode})</span>
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div className="inventory-form-group inventory-form-span-full role-permission-matrix">
                  <span className="user-role-checkboxes-label">Functions</span>
                  <div className="user-role-checkboxes-row">
                    {functionPermissions.length === 0 ? (
                      <span className="inventory-items-hint">No function permissions in catalog.</span>
                    ) : (
                      functionPermissions.map((perm) => (
                        <label key={perm.permissionCode} className="inventory-checkbox-label">
                          <input
                            type="checkbox"
                            checked={form.permissionCodes.includes(perm.permissionCode)}
                            onChange={() => togglePermission(perm.permissionCode)}
                          />
                          <span>
                            {perm.name}
                            <span className="role-permission-code"> ({perm.permissionCode})</span>
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div className="inventory-form-actions inventory-form-span-full">
                  <button type="submit" className="add-button" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving…' : editingRole ? 'Update Role' : 'Add Role'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default RoleAccessAdmin;
